import React, { FunctionComponent } from 'react';
import { startsWith } from 'ramda';
import { createScriptNode } from '../../utils/dom';
import { setQueryStringParameter } from '../../utils/url';

const HubSpotPortalId = '498216';
const HubSpotElementId = 'hs-script-loader';

const w: any = window;
// eslint-disable-next-line no-underscore-dangle, no-multi-assign
const _hsp = (w._hsp = w._hsp || []);
// eslint-disable-next-line no-underscore-dangle, no-multi-assign
const _hsq = (w._hsq = w._hsq || []);

const HubSpot = {
  execute: (action: () => void) => {
    // If external API methods are already available, use them.
    if (w.HubSpotConversations) {
      action();
      return;
    }
    /*
      Otherwise, callbacks can be added to the hsConversationsOnReady on the window object.
      These callbacks will be called once the external API has been initialized.
    */
    w.hsConversationsOnReady = w.hsConversationsOnReady || [];
    w.hsConversationsOnReady.push(action);
  },

  resetWidget: () => {
    const status = w.HubSpotConversations?.widget.status();
    if (status?.loaded) {
      _hsp.push(['revokeCookieConsent']);
      w.HubSpotConversations.clear({ resetWidget: true });
    }
  },

  loadWidget: () => {
    const status = w.HubSpotConversations?.widget.status();

    if (status?.loaded) {
      w.HubSpotConversations.widget.refresh({ openToNewThread: true });
    } else {
      w.HubSpotConversations.widget.load({ widgetOpen: false });
    }
  },

  setUserIdentity: ({
    email,
    accountId,
  }: {
    email: string;
    accountId: number;
  }) => {
    const djangolink = `http://dashboard.inventorum.com/admin/accounts/account/${accountId}`;

    // Identify current user on chat
    _hsq.push([
      'identify',
      {
        email,
        djangolink__pos_: djangolink,
      },
    ]);
  },

  setChatflow: ({
    isTrialUser,
    locale,
  }: {
    locale: string;
    isTrialUser?: boolean;
  }) => {
    const chatflowUser = isTrialUser ? 'trial' : 'paid';
    const chatflowLocale = ['en', 'es', 'de'].some(startsWith(locale))
      ? locale.slice(0, 2)
      : 'zu';
    const chatFlow: string = `pos_${chatflowUser}_${chatflowLocale}`;

    // Force the widget to open to a specific chat flow with targeted query string
    setQueryStringParameter({
      name: 'chatflow',
      value: chatFlow,
      append: true,
    });
  },
};

type HubSpotWidgetProps = {
  email?: string;
  locale: string;
  accountId?: number;
  isTrialUser?: boolean;
};
const HubSpotWidget: FunctionComponent<HubSpotWidgetProps> = ({
  email,
  locale,
  accountId,
  isTrialUser,
}) => {
  const onConversationsAPIReady = () => {
    if (email && accountId) {
      HubSpot.setUserIdentity({ email, accountId });
      HubSpot.setChatflow({ locale, isTrialUser });
      HubSpot.loadWidget();
    }
    return HubSpot.loadWidget();
  };

  React.useEffect(() => {
    w.hsConversationsSettings = { loadImmediately: true };

    const script = document.getElementById(HubSpotElementId);

    if (!script) {
      const scriptNode = createScriptNode({
        src: `//js.hs-scripts.com/${HubSpotPortalId}.js`,
        id: HubSpotElementId,
        async: true,
        defer: true,
      });

      document.body.appendChild(scriptNode);
    }

    // Load Widget when ready
    HubSpot.execute(onConversationsAPIReady);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, accountId]);

  return null;
};

// eslint-disable-next-line import/prefer-default-export
export { HubSpot, HubSpotWidget };
